/**
 * API service — typed wrapper around the Fussaki FastAPI backend.
 *
 * All calls go to REACT_APP_API_URL (localhost:8000 in dev, api.fussaki.com in prod).
 */

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// --- Types mirroring the FastAPI schemas ---

export type BookingType = 'open_party' | 'private_charter';
export type CharterType = 'half-day' | 'full-day' | 'specialty' | 'custom';
export type BookingStatus = 'pending' | 'confirmed' | 'cancelled';

export interface CreateBookingPayload {
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  booking_type: BookingType;
  charter_type: CharterType;
  booking_date: string; // ISO datetime string: "2026-06-01T07:00:00"
  spots_requested: number;
  how_did_you_hear?: string;
  message?: string;
}

export interface BookingResponse {
  id: number;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  booking_type: BookingType;
  charter_type: CharterType;
  booking_date: string;
  spots_requested: number;
  how_did_you_hear: string | null;
  message: string | null;
  status: BookingStatus;
  calendar_event_id: string | null;
  created_at: string;
  updated_at: string;
  email_sent: boolean | null;
}

export interface DateAvailability {
  date: string;
  available: boolean;
  open_party_spots_taken: number;
  open_party_spots_remaining: number;
  has_pending_private: boolean;
  has_confirmed_private: boolean;
  capacity: number;
}

export interface AvailabilityResponse {
  blocked_dates: string[];                        // "YYYY-MM-DD" strings
  date_details: Record<string, DateAvailability>; // keyed by "YYYY-MM-DD"
}

// --- API error type ---

export class ApiError extends Error {
  constructor(public status: number, public detail: string) {
    super(detail);
  }
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let detail = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      detail = body.detail || detail;
    } catch {}
    throw new ApiError(res.status, detail);
  }
  return res.json();
}

// --- Public API functions ---

export async function fetchAvailability(): Promise<AvailabilityResponse> {
  const res = await fetch(`${API_BASE}/api/v1/calendar/availability`);
  return handleResponse<AvailabilityResponse>(res);
}

export async function createBooking(payload: CreateBookingPayload): Promise<BookingResponse> {
  const res = await fetch(`${API_BASE}/api/v1/bookings`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handleResponse<BookingResponse>(res);
}

// --- Contact Inquiry ---

export interface ContactInquiryPayload {
  name: string;
  email: string;
  phone?: string;
  inquiry_type: string;
  message: string;
}

export interface ContactInquiryResponse {
  success: boolean;
  message: string;
}

export async function submitContactInquiry(payload: ContactInquiryPayload): Promise<ContactInquiryResponse> {
  const res = await fetch(`${API_BASE}/api/v1/contact`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handleResponse<ContactInquiryResponse>(res);
}
